import type {
  Balance,
  Bet,
  CurrencySpec,
  EvidenceBundle,
  GameDefinition,
  GameId,
  GameRules,
  GameRoundId,
  LaunchSession,
  PlaceBetCommand,
  PrivateEventEnvelope,
  PublicEventEnvelope,
  RoundSnapshot,
  VehicleClass,
  Money,
  Promotion,
} from '@urbanrats/contracts';
import { err, ok, type PlaceBetError, type Result } from '@urbanrats/domain';
import { NetworkError, type ClientGateways, type ClientRoot, type ConnectionState, type DropPrize, type EngagementView, type GameInfo, type PlaybackTimelineEvent, type PlayerStatsView, type PromoClient } from './types.ts';
import { estimateOffset, type ClockEstimate, type ClockSample } from '../lib/playbackSync.ts';

/**
 * Real backend wiring (HTTP + WebSocket) with the same contracts as the demo gateway. The session
 * token comes from a one-time operator launch code (`?launch=`), is kept for this tab only
 * (sessionStorage) and removed from the URL. Nothing here decides odds, counts or payouts.
 */
const API = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://127.0.0.1:8787';
const TOKEN_KEY = 'urbanrats.session.v1';

/** No browser-side engine: there is nothing to lock. */
export async function acquireDemoLock(): Promise<{ lost: Promise<void> }> {
  return { lost: new Promise(() => undefined) };
}

interface SessionView extends LaunchSession {
  gameId: GameId;
  allowedOrigin: string | null;
  limits: {
    minStakeAtomic: string;
    maxStakeAtomic: string;
    payoutCap: Money;
  } | null;
  game: {
    gameId: GameId;
    title: string;
    rulesText: string;
    rules: GameRules;
    includedClasses: VehicleClass[];
    mechanic: string;
  } | null;
  navigation?: {
    lobbyUrl: string | null;
    depositUrl: string | null;
    returnUrl: string | null;
  };
  funPlay?: boolean;
}

class Http {
  private readonly token: string;
  constructor(token: string) {
    this.token = token;
  }
  async req<T>(method: string, path: string, body?: unknown): Promise<{ status: number; body: T }> {
    let r: Response;
    try {
      r = await fetch(`${API}${path}`, {
        method,
        headers: {
          authorization: `Bearer ${this.token}`,
          ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });
    } catch {
      throw new NetworkError();
    }
    const text = await r.text();
    return { status: r.status, body: (text ? JSON.parse(text) : null) as T };
  }
  async get<T>(path: string): Promise<T> {
    const r = await this.req<T>('GET', path);
    if (r.status === 401) throw new SessionError();
    if (r.status >= 400) throw new Error(`GET ${path}: ${r.status}`);
    return r.body;
  }
}

class SessionError extends Error {
  constructor() {
    super('session expired or invalid');
    this.name = 'SessionError';
  }
}

async function obtainToken(): Promise<string> {
  const url = new URL(location.href);
  const code = url.searchParams.get('launch');
  if (code) {
    // One-time code: exchange, then remove it from the URL and history (never logged or bookmarked).
    url.searchParams.delete('launch');
    history.replaceState(null, '', url.pathname + url.search + url.hash);
    const r = await fetch(`${API}/v1/player/sessions`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ launchCode: code }),
    }).catch(() => null);
    if (!r?.ok) throw new Error('This launch link is invalid or has already been used. Please open the game again from your operator.');
    const s = (await r.json()) as { sessionToken: string };
    sessionStorage.setItem(TOKEN_KEY, s.sessionToken);
    return s.sessionToken;
  }
  const t = sessionStorage.getItem(TOKEN_KEY);
  if (!t) {
    // Demo server only (build-time VITE_NO_SESSION_URL): open a fresh launch instead of the error page.
    // `{guest}` becomes a per-tab guest id; real operator builds leave the variable unset.
    const fallback = import.meta.env.VITE_NO_SESSION_URL as string | undefined;
    if (fallback) {
      location.replace(fallback.replace('{guest}', `guest-${crypto.randomUUID().slice(0, 8)}`));
      return new Promise<string>(() => undefined);
    }
    throw new Error('No game session. Please open the game from your operator’s site.');
  }
  return t;
}

/** Chip presets derived from the table limits (UI convenience only; the server enforces limits). */
function chipsFor(limits: SessionView['limits'], scale: number): string[] {
  const unit = 10n ** BigInt(Math.max(0, scale - 1)); // 0.1 of a unit
  const min = limits ? BigInt(limits.minStakeAtomic) : unit;
  const max = limits ? BigInt(limits.maxStakeAtomic) : unit * 1000n;
  const base = min > unit ? min : unit;
  return [1n, 5n, 10n, 50n, 100n, 500n]
    .map((m) => base * m)
    .filter((v) => v <= max)
    .map(String);
}

type Sub<T> = Set<(e: T) => void>;

/**
 * One WebSocket for the session: public round topic + private player topic. Tracks the last sequence per
 * topic; on a gap or reconnect it resubscribes with `after` (bounded replay). If the server says
 * snapshot-required, subscribers get a synthetic resync signal (sequence jump) and re-read over HTTP.
 */
class Stream {
  private ws: WebSocket | null = null;
  private last: Record<'round' | 'player', number | null> = {
    round: null,
    player: null,
  };
  readonly pub: Sub<PublicEventEnvelope> = new Set();
  readonly prv: Sub<PrivateEventEnvelope> = new Set();
  readonly conn: Sub<ConnectionState> = new Set();
  state: ConnectionState = 'reconnecting';
  private retry = 0;
  private closed = false;
  private readonly token: string;

  constructor(token: string) {
    this.token = token;
    this.open();
  }

  private setState(s: ConnectionState) {
    if (this.state === s) return;
    this.state = s;
    for (const h of this.conn) h(s);
  }

  private subscribe(stream: 'round' | 'player') {
    const after = this.last[stream];
    this.ws?.send(
      JSON.stringify({
        type: 'subscribe',
        stream,
        ...(after !== null ? { after } : {}),
      }),
    );
  }

  private open() {
    if (this.closed) return;
    const ws = new WebSocket(`${API.replace(/^http/, 'ws')}/v1/stream`);
    this.ws = ws;
    ws.onopen = () => ws.send(JSON.stringify({ type: 'auth', token: this.token }));
    ws.onmessage = (m) => {
      const f = JSON.parse(String(m.data)) as {
        type: string;
        topic?: 'round' | 'player';
        envelope?: PublicEventEnvelope & PrivateEventEnvelope;
        head?: number;
      };
      if (f.type === 'authenticated') {
        this.retry = 0;
        this.subscribe('round');
        this.subscribe('player');
        return;
      }
      if (f.type === 'subscribed') {
        this.setState('connected');
        if (f.topic && this.last[f.topic] === null) this.last[f.topic] = f.head ?? 0;
        return;
      }
      if (f.type === 'snapshot' && f.envelope && f.topic === 'round') {
        this.last.round = f.envelope.sequence;
        for (const h of this.pub) h(f.envelope);
        return;
      }
      if (f.type === 'snapshot-required' && f.topic) {
        // Too far behind: restart from the head; listeners re-read state (sequence jump triggers resync).
        this.last[f.topic] = null;
        // A reconnect cycle makes the game re-read bets/balance over HTTP (useGame resyncs on 'connected').
        this.setState('reconnecting');
        this.subscribe(f.topic);
        return;
      }
      if (f.type === 'event' && f.envelope && f.topic) {
        const prev = this.last[f.topic];
        if (prev !== null && f.envelope.sequence <= prev) return; // duplicate (replay overlap)
        if (prev !== null && f.envelope.sequence !== prev + 1) {
          // Gap: resume from what we have instead of guessing.
          this.subscribe(f.topic);
          return;
        }
        this.last[f.topic] = f.envelope.sequence;
        if (f.topic === 'round') for (const h of this.pub) h(f.envelope);
        else for (const h of this.prv) h(f.envelope);
      }
    };
    ws.onclose = (e) => {
      this.ws = null;
      if (this.closed) return;
      if (e.code === 4401) {
        for (const h of this.prv)
          h({
            protocolVersion: 1,
            eventId: 'session-expired',
            sequence: (this.last.player ?? 0) + 1,
            occurredAt: new Date().toISOString() as never,
            gameRoundId: null,
            payload: { type: 'session.expired' },
          });
        this.setState('offline');
        return;
      }
      this.setState(this.retry > 3 ? 'offline' : 'reconnecting');
      const delay = Math.min(10_000, 500 * 2 ** this.retry++);
      setTimeout(() => this.open(), delay);
    };
  }

  close() {
    this.closed = true;
    this.ws?.close();
  }
}

export async function createGateways(): Promise<ClientRoot> {
  const token = await obtainToken();
  const http = new Http(token);
  const session = await http.get<SessionView>('/v1/player/session').catch((e: unknown) => {
    sessionStorage.removeItem(TOKEN_KEY);
    throw e instanceof SessionError ? new Error('Your game session has ended. Please open the game again from your operator.') : e;
  });
  // Server clock (display, countdowns and synchronised playback — never acceptance): several samples,
  // the one with the smallest round trip wins; re-estimated every minute.
  const sampleClock = async () => {
    const samples: ClockSample[] = [];
    for (let i = 0; i < 5; i++) {
      const sentAt = Date.now();
      const r = await fetch(`${API}/v1/time`, { cache: 'no-store' })
        .then((x) => x.json() as Promise<{ serverTime: string }>)
        .catch(() => null);
      if (r)
        samples.push({
          sentAt,
          receivedAt: Date.now(),
          serverTime: Date.parse(r.serverTime),
        });
    }
    return estimateOffset(samples);
  };
  let clock: ClockEstimate = (await sampleClock()) ?? {
    offsetMs: 0,
    uncertaintyMs: Infinity,
  };
  const clockTimer = setInterval(() => {
    void sampleClock().then((c) => {
      if (c && c.uncertaintyMs <= clock.uncertaintyMs * 2 + 50) clock = c;
    });
  }, 60_000);
  const defs = (await http.get<{ games: GameDefinition[] }>('/v1/games')).games;
  const def = defs.find((g) => g.gameId === session.gameId)!;
  const cur: CurrencySpec = session.walletCurrency;
  const stream = new Stream(token);
  const g = session.game!;
  const info: GameInfo = {
    gameId: g.gameId,
    title: g.title,
    mechanic: g.mechanic,
    rulesText: g.rulesText,
    rules: g.rules,
    videoSources: [],
    includedClasses: g.includedClasses,
  };
  // Inventory (freebets / boosts) from the server; refreshed periodically and after bets and drops.
  let rewardItems: Promotion[] = [];
  const rewardListeners = new Set<() => void>();
  const refreshRewards = async () => {
    const r = await http.req<{ items: Promotion[] }>('GET', '/v1/player/rewards').catch(() => null);
    if (r?.status === 200) {
      rewardItems = r.body.items;
      for (const h of rewardListeners) h();
    }
  };
  void refreshRewards();
  const rewardTimer = setInterval(() => void refreshRewards(), 20_000);
  const promo: PromoClient | null = session.limits
    ? {
        flags: {
          inventory: true,
          freebets: true,
          boosts: true,
          lightning: false,
          level: false,
          dailyCheckin: false,
          streak: false,
          comeback: false,
          realMoney: false,
        },
        policy: {
          policyVersion: 'promo-math-v1',
          formula: 'floor(stake*oN*lN*(10000+bps)/(oD*lD*10000)); min(gross,cap)',
          payoutCap: session.limits.payoutCap,
          maxBoostBpsPerBet: 2500,
          maxRewardsPerBet: 1,
          freebetWithLightning: true,
          boostWithLightning: true,
          voidPolicy: 'RESTORE_IF_NOT_EXPIRED',
          realMoneyEnabled: false,
        },
        lightningPolicy: null,
        items: () => rewardItems,
        level: () => null,
        checkinState: () => null,
        checkin: async () => ({ ok: false as const, error: 'not available' }),
        subscribe: (h) => {
          rewardListeners.add(h);
          return () => rewardListeners.delete(h);
        },
      }
    : null;
  // Play time for leaderboards: a heartbeat every 30 s while the tab is visible and the player was active.
  let lastInput = Date.now();
  const markInput = () => (lastInput = Date.now());
  for (const e of ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const) addEventListener(e, markInput, { passive: true });
  const heartbeat = setInterval(() => {
    const active = document.visibilityState === 'visible' && Date.now() - lastInput < 120_000;
    void http.req('POST', '/v1/player/heartbeat', { active }).catch(() => undefined);
  }, 30_000);

  const gw: ClientGateways = {
    kind: 'production',
    gameInfo: info,
    currency: cur,
    session,
    fx: { quotes: () => [], displayCurrencies: [] },
    promo,
    engagement: session.funPlay
      ? undefined
      : {
          get: async () => {
            const r = await http.req<EngagementView>('GET', '/v1/player/engagement').catch(() => null);
            return r?.status === 200 ? r.body : null;
          },
          openDrop: async (id) => {
            const r = await http.req<{ prize: DropPrize } | { code: string; message: string }>('POST', `/v1/player/drops/${encodeURIComponent(id)}/open`, {});
            if (r.status === 200 && 'prize' in r.body) {
              void refreshRewards();
              return { ok: true as const, prize: r.body.prize };
            }
            return {
              ok: false as const,
              error: 'code' in r.body ? r.body.code : 'ERROR',
            };
          },
        },
    funPlay: session.funPlay === true,
    playerStats: session.funPlay
      ? undefined
      : async () => {
          const r = await http.req<PlayerStatsView>('GET', '/v1/player/stats').catch(() => null);
          return r?.status === 200 ? r.body : null;
        },
    rulesIntro: {
      get: async () => {
        const r = await http.req<{ version: string; show: boolean; dontShow: boolean }>('GET', '/v1/player/rules-intro').catch(() => null);
        return r?.status === 200 ? r.body : null;
      },
      accept: async (version, dontShow) => {
        const r = await http.req<{ code?: string }>('POST', '/v1/player/rules-intro', { version, dontShow }).catch(() => null);
        return r?.status === 200 ? { ok: true as const } : { ok: false as const, error: r?.body.code ?? 'NETWORK' };
      },
    },
    stakeLimits: {
      minAtomic: session.limits?.minStakeAtomic ?? '1',
      maxAtomic: session.limits?.maxStakeAtomic ?? '1',
    },
    chipsAtomic: chipsFor(session.limits, cur.scale),
    game: {
      listGames: async () => [def],
      getCurrentRound: () => http.get<RoundSnapshot | null>('/v1/player/rounds/current'),
      getRound: (id: GameRoundId) => http.get<RoundSnapshot>(`/v1/player/rounds/${encodeURIComponent(id)}`).catch(() => null),
      subscribePublic: (h) => {
        stream.pub.add(h);
        return () => stream.pub.delete(h);
      },
      subscribePrivate: (h) => {
        stream.prv.add(h);
        return () => stream.prv.delete(h);
      },
      placeBet: async (cmd: PlaceBetCommand): Promise<Result<Bet, PlaceBetError>> => {
        const r = await http.req<Bet | { code: string; message: string; bet: Bet | null }>('POST', '/v1/player/bets', cmd);
        if (r.status === 200) return ok(r.body as Bet);
        const e = r.body as { code: string; message: string; bet: Bet | null };
        if (r.status === 422 || r.status === 409)
          return err({
            code: e.code as PlaceBetError['code'],
            bet: e.bet ?? null,
            message: e.message,
          } as PlaceBetError);
        // Rate-limited: the command had no effect; the same commandId can be retried safely.
        if (r.status === 429) throw new NetworkError('rate limited');
        throw new NetworkError(`bet ${r.status}`);
      },
      listBets: async () => (await http.get<{ bets: Bet[] }>('/v1/player/bets')).bets,
      listRecentRounds: async (limit: number) => (await http.get<{ rounds: RoundSnapshot[] }>(`/v1/player/rounds?limit=${limit}`)).rounds,
    },
    wallet: { getBalance: () => http.get<Balance>('/v1/player/balance') },
    stream: {
      getStream: async () => null,
      subscribeHealth: () => () => undefined,
    },
    evidence: {
      getEvidence: (id) => http.get<EvidenceBundle | null>(`/v1/player/evidence/${encodeURIComponent(id)}`),
    },
    connection: {
      get: () => stream.state,
      subscribe: (h) => {
        stream.conn.add(h);
        return () => stream.conn.delete(h);
      },
    },
    media: {
      listSources: async () => [],
      setVideoMode: () => undefined,
      // Video rounds are counted server-side on the private ingest; the browser never counts.
      startVideoCounting: () => {
        throw new Error('video counting runs on the server for this backend');
      },
      getLiveRelay: async (id) => {
        const r = await http.req<{
          playlistUrl: string;
          serverTime: string;
          behindMs: number | null;
          counter: string;
          earlier: number[];
          segmentMs: number | null;
        }>('POST', `/v1/player/rounds/${encodeURIComponent(id)}/live`);
        return r.status === 200 ? { ...r.body, playlistUrl: `${API}${r.body.playlistUrl}` } : null;
      },
      getRoundSummary: async (id) => {
        const r = await http.req<{
          bets: number;
          players: number;
          winners: number;
          totalStake: Money;
          totalPaid: Money;
        }>('GET', `/v1/player/rounds/${encodeURIComponent(id)}/summary`);
        return r.status === 200 ? r.body : null;
      },
      getLivePlayback: async (id) => {
        const r = await http.req<{
          playlistUrl: string;
          startAt: string;
          revealAt: string;
          serverTime: string;
          events: PlaybackTimelineEvent[];
        }>('POST', `/v1/player/rounds/${encodeURIComponent(id)}/playback`);
        return r.status === 200
          ? {
              ...r.body,
              playlistUrl: r.body.playlistUrl.startsWith('http') ? r.body.playlistUrl : `${API}${r.body.playlistUrl}`,
            }
          : null;
      },
      getReplay: async (id) => {
        const r = await http.req<{
          playlistUrl: string;
          ptsOffsetMicros: number;
          expiresAt: string;
        }>('POST', `/v1/player/replay/${encodeURIComponent(id)}`);
        return r.status === 200 ? { ...r.body, playlistUrl: `${API}${r.body.playlistUrl}` } : null;
      },
    },
    navigation: session.navigation ?? {
      lobbyUrl: null,
      depositUrl: null,
      returnUrl: null,
    },
    serverNowMs: () => Date.now() + clock.offsetMs,
    clockUncertaintyMs: () => clock.uncertaintyMs,
    dispose: () => {
      clearInterval(rewardTimer);
      clearInterval(heartbeat);
      clearInterval(clockTimer);
      stream.close();
    },
  };
  return {
    kind: 'production',
    games: [info],
    forGame: () => gw,
    activate: () => undefined,
    embedAllowedOrigins: session.allowedOrigin ? [session.allowedOrigin] : [],
    walletCurrency: cur.code,
    moneyMode: session.moneyMode ?? 'DEMO',
    dispose: () => stream.close(),
  };
}
